// Chrome registry — the single source of truth mapping a route to its chrome.
// Design: docs/FeatureDesigns/hybrid-shell-chrome-contract.md §4.2
//
// The web layouts read this to decide chrome; the native shell mirrors it
// (mobile/ios/BuzzBrain/Shell/PathConfiguration.swift — keep in sync). It
// generalizes the ad-hoc `isFullscreen` booleans in AppLayout/AppLayout.

import type { ChromeDescriptor, TabId } from './chrome';

interface TabDef {
  id: TabId;
  title: string;
  path: string;
}

/**
 * EVERY path that can be a top-level tab root — including CONDITIONAL tabs that
 * exist only in the live `setTabs` model (Assigned, for teacher-linked
 * students). Chrome lookup must consult this full list, not the static baseline
 * below: a conditional tab missing here falls through to the L2 branch, so
 * native draws a header + back and the page visibly shifts down by the nav bar.
 * Keep in sync with `PathConfiguration.allTabRoots` (Swift).
 *
 * Not tabs: `/you` (reached via Home's profile avatar) and `/bookmarks` (a tile
 * inside Practice) — both are L2 detail screens, and get a header + back below.
 */
const ALL_TAB_ROOTS: readonly TabDef[] = [
  { id: 'home', title: 'Home', path: '/home' },
  { id: 'progress', title: 'Progress', path: '/plan' },
  { id: 'assignments', title: 'Assigned', path: '/assigned' },
  { id: 'practice', title: 'Practice', path: '/practice' },
  { id: 'learning-moments', title: 'Insights', path: '/learning-moments' },
];

/** Tabs that appear only when the live model reports them. */
const CONDITIONAL_TAB_IDS = new Set<TabId>(['assignments']);

/**
 * Top-level (L1) pages that are NOT tabs: they show the tab bar but no tab is
 * selected, and you leave via the tab bar rather than a back button — exactly
 * like the web, where `/you` (reached from Home's profile avatar) is an L1 page
 * with the bottom nav visible and no back. Must match AppLayout's
 * `L1_PATHS` minus the tab-root paths. Everything not listed here or in
 * ALL_TAB_ROOTS is an L2 detail screen that HIDES the tab bar.
 */
const L1_NON_TAB_ROOTS = new Set<string>(['/you']);

/**
 * Baseline top-level tabs — the pre-report fallback native renders before web JS
 * runs. The live model arrives via the `setTabs` bridge (derived from
 * AppLayout's navItems). Derived from ALL_TAB_ROOTS minus the conditional
 * ones, so it mirrors the no-teacher case and the common path never reshuffles
 * the bar after load.
 */
export const SHELL_TABS: readonly TabDef[] = ALL_TAB_ROOTS.filter(
  (t) => !CONDITIONAL_TAB_IDS.has(t.id),
);

/**
 * Immersive (no chrome) routes — mirrors the web app's existing `isFullscreen`
 * intent (AppLayout `isQuestionPage`): the solver, placement, and live-duel
 * match are full-screen takeovers. (The `/battle/friends` and
 * `/battle/leaderboard` browse screens stay in-layout.)
 */
export function isImmersivePath(pathname: string): boolean {
  if (
    pathname.startsWith('/question/') ||
    pathname.startsWith('/session/') ||
    pathname.startsWith('/student-placement')
  ) {
    return true;
  }
  if (
    pathname.startsWith('/battle/') &&
    pathname !== '/battle/friends' &&
    pathname !== '/battle/leaderboard'
  ) {
    return true;
  }
  return false;
}

/**
 * Auth / pre-shell routes rendered OUTSIDE the sidebar layout (login, register,
 * impersonate, first-run, photo-upload). These get no native chrome at all.
 */
const NO_CHROME_EXACT = new Set([
  '/',
  '/login',
  '/impersonate',
  '/complete-registration',
  '/first-run',
]);
const NO_CHROME_PREFIXES = ['/upload/', '/bulk-upload/'];

export function isNoChromePath(pathname: string): boolean {
  return NO_CHROME_EXACT.has(pathname) || NO_CHROME_PREFIXES.some((p) => pathname.startsWith(p));
}

export function getChromeForPath(pathname: string): ChromeDescriptor {
  if (isNoChromePath(pathname)) {
    return { navStyle: 'none', header: { mode: 'none' } };
  }

  if (isImmersivePath(pathname)) {
    return { navStyle: 'none', immersive: true, header: { mode: 'none' } };
  }

  const tab = ALL_TAB_ROOTS.find((t) => t.path === pathname);
  if (tab) {
    return {
      navStyle: 'tabbar',
      selectedTab: tab.id,
      header: { mode: 'none' },
    };
  }

  // L1 non-tab page (/you): tab bar visible, no selected tab, no header — you
  // leave via the tab bar, as on web.
  if (L1_NON_TAB_ROOTS.has(pathname)) {
    return { navStyle: 'tabbar', header: { mode: 'none' } };
  }

  // Everything else is an L2 detail screen. Hide the tab bar (matching web
  // mobile, which drops the bottom nav on detail pages) — AppLayout only
  // reserves the tab-bar bottom inset for L1 pages, so the two stay in step. No
  // native header either: the web renders its own PageHeader + back button, and
  // these native nav stacks stay at depth 1 (web nav is client-side; the
  // `navigate` push is unused), so a native header could show neither a title
  // nor a back chevron — only an empty bar. Keep in sync with
  // PathConfiguration.swift.
  return { navStyle: 'none', header: { mode: 'none' } };
}
